import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, LogOut, Building2, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { api } from "@/lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const { code, setCode, options } = useCurrency();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminUnread, setAdminUnread] = useState(0);

  // Iter62 · Admin badge count. Polls every 60s while the admin is signed in.
  useEffect(() => {
    if (!user || user.role !== "admin") { setAdminUnread(0); return; }
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/notifications/unread-count");
        if (alive) setAdminUnread(data.unread_count || 0);
      } catch (_) { /* noop */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [user]);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 md:gap-8 h-16">
          <Link to="/" className="flex items-center" data-testid="header-home-link" aria-label="Accommodation Suppliers — home">
            <img src="/brand/as-logo-horizontal.png" alt="Accommodation Suppliers" className="h-8 sm:h-9 md:h-10 w-auto" data-testid="header-logo" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm text-[#4A5550]">
            <Link to="/for-hotels" className="hover:text-[#264839]" data-testid="nav-for-hotels">For Hotels</Link>
            <Link to="/categories" className="hover:text-[#264839]" data-testid="nav-categories">Categories</Link>
            <Link to="/search" className="hover:text-[#264839]" data-testid="nav-search">Search</Link>
            <Link to="/resources" className="hover:text-[#264839]" data-testid="nav-resources">Resources</Link>
            <Link to="/pricing" className="hover:text-[#264839]" data-testid="nav-pricing">For Suppliers</Link>
          </nav>

          <form onSubmit={submit} className="hidden md:flex flex-1 max-w-md ml-auto relative" data-testid="header-search-form">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8C85]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products or supplier names"
              className="w-full pl-9 pr-4 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm focus:border-[#264839]" data-testid="header-search-input" />
          </form>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-3 ml-auto md:ml-0">
            <select value={code} onChange={(e) => setCode(e.target.value)}
              className="text-xs border border-[#C4CFC9] bg-white px-2 py-1.5 rounded-sm text-[#11211A] cursor-pointer" data-testid="currency-selector" aria-label="Currency">
              {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#264839] relative" data-testid="nav-admin">
                    <ShieldCheck className="w-4 h-4" /> Admin
                    {adminUnread > 0 && (
                      <span className="bg-[#B35C4A] text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums leading-none" data-testid="nav-admin-badge">
                        {adminUnread > 99 ? "99+" : adminUnread}
                      </span>
                    )}
                  </Link>
                )}
                <Link to={user.role === "buyer" ? "/buyer" : "/dashboard"} className="inline-flex items-center gap-1 text-sm text-[#264839]" data-testid="nav-dashboard">
                  <Building2 className="w-4 h-4" /> {user.role === "buyer" ? "My RFQs" : "Dashboard"}
                </Link>
                <button onClick={async () => { await logout(); navigate("/"); }} className="text-sm text-[#7D8C85] hover:text-[#11211A]" data-testid="nav-logout" aria-label="Sign out">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm text-[#4A5550] hover:text-[#264839]" data-testid="nav-login">Sign in</Link>
                <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">List your products</Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((s) => !s)}
            className="md:hidden ml-auto p-2 text-[#11211A] hover:bg-[#F0EFEA] rounded-sm"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-[#E8ECE9] py-4 space-y-4" data-testid="mobile-menu">
            <form onSubmit={submit} className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8C85]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products or supplier names"
                className="w-full pl-9 pr-4 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm focus:border-[#264839]" data-testid="mobile-search-input" />
            </form>
            <nav className="flex flex-col gap-3 text-[15px] text-[#11211A]">
              <Link to="/for-hotels" onClick={() => setMobileOpen(false)} className="py-1" data-testid="mobile-nav-for-hotels">For Hotels</Link>
              <Link to="/categories" onClick={() => setMobileOpen(false)} className="py-1" data-testid="mobile-nav-categories">Categories</Link>
              <Link to="/search" onClick={() => setMobileOpen(false)} className="py-1" data-testid="mobile-nav-search">Search</Link>
              <Link to="/resources" onClick={() => setMobileOpen(false)} className="py-1" data-testid="mobile-nav-resources">Resources</Link>
              <Link to="/pricing" onClick={() => setMobileOpen(false)} className="py-1" data-testid="mobile-nav-pricing">For Suppliers</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-1" data-testid="mobile-nav-contact">Contact</Link>
            </nav>
            <div className="pt-3 border-t border-[#E8ECE9] flex items-center gap-3 flex-wrap">
              <select value={code} onChange={(e) => setCode(e.target.value)}
                className="text-xs border border-[#C4CFC9] bg-white px-2 py-1.5 rounded-sm text-[#11211A]" data-testid="mobile-currency-selector" aria-label="Currency">
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              {user ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-sm text-[#264839] inline-flex items-center gap-1" data-testid="mobile-nav-admin">
                      <ShieldCheck className="w-4 h-4" /> Admin
                      {adminUnread > 0 && (
                        <span className="bg-[#B35C4A] text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums leading-none" data-testid="mobile-nav-admin-badge">
                          {adminUnread > 99 ? "99+" : adminUnread}
                        </span>
                      )}
                    </Link>
                  )}
                  <Link to={user.role === "buyer" ? "/buyer" : "/dashboard"} onClick={() => setMobileOpen(false)} className="text-sm text-[#264839] inline-flex items-center gap-1" data-testid="mobile-nav-dashboard">
                    <Building2 className="w-4 h-4" /> {user.role === "buyer" ? "My RFQs" : "Dashboard"}
                  </Link>
                  <button onClick={async () => { await logout(); navigate("/"); setMobileOpen(false); }} className="text-sm text-[#7D8C85] inline-flex items-center gap-1" data-testid="mobile-nav-logout">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="text-sm text-[#4A5550]" data-testid="mobile-nav-login">Sign in</Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="btn-primary text-sm" data-testid="mobile-nav-register">List your products</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
